import { Button, Divider, Flex, Popover, theme, Tooltip } from 'antd'
import type { ButtonProps } from 'antd'
import { cx } from 'antd-style'
import { ChevronLeft, Ellipsis, X } from 'lucide-react'
import { AnimatePresence, motion } from 'motion/react'
import type { CSSProperties, ReactNode } from 'react'
import { memo, useState } from 'react'
import { createPortal } from 'react-dom'
import { useHotkeys } from 'react-hotkeys-hook'
import { useTranslation } from 'react-i18next'

import { useIconSizes } from 'src/hooks'

import { InfoTooltip } from '../InfoTooltip'

import { BulkActionsOverflow } from './BulkActionsOverflow'
import { useStyles } from './styles'
import { useActionOverflow } from './useActionOverflow'

export type BulkAction = {
  title: string
  hint?: string
  buttonProps?: ButtonProps
  popover?: (close: () => void) => ReactNode
  fillsPopover?: boolean
}

type Props = {
  count: number
  actions: BulkAction[]
  onDeselect: () => void
  entity?: string
  selectAll?: ReactNode
  offsetBottom?: number
}

const DEFAULT_OFFSET = 24
const CLOSE_ICON_SIZE = 16
const BAR_Z_INDEX = 2000
const MORE_KEY = '__more__'

const positionStyles: CSSProperties = {
  position: 'fixed',
  bottom: DEFAULT_OFFSET,
  left: DEFAULT_OFFSET,
  right: DEFAULT_OFFSET,
  zIndex: BAR_Z_INDEX,
  pointerEvents: 'none'
} as const

export const BulkActionsBar = memo(
  ({ count, actions, onDeselect, entity, selectAll }: Props) => {
    const { t } = useTranslation()
    const { styles } = useStyles()
    const { token } = theme.useToken()

    const iconSizes = useIconSizes()

    const [openPopover, setOpenPopover] = useState<string | null>(null)
    const [overflowAction, setOverflowAction] = useState<string | null>(null)

    const summary = entity
      ? t('entity_selected', { count, entity })
      : t('count_selected', { count })

    const pinned = actions.map(({ buttonProps }) =>
      Boolean(buttonProps?.danger)
    )

    const { roomRef, barRef, actionsRef, measureRef, overflowed } =
      useActionOverflow(token.marginXXS, 0, pinned, summary.length)

    const fitted = overflowed.length === actions.length ? overflowed : []

    const isOverflowed = (index: number) => fitted[index] && !pinned[index]

    const visibleActions = actions.filter((_, index) => !isOverflowed(index))
    const overflowActions = actions.filter((_, index) => isOverflowed(index))

    const panels = [
      ...visibleActions
        .filter(({ popover }) => popover)
        .map(({ title }) => title),
      ...(overflowActions.length ? [MORE_KEY] : [])
    ]

    const openPanel =
      openPopover && panels.includes(openPopover) ? openPopover : null

    const isVisible = count > 0 || openPanel !== null

    const closeOverflow = () => {
      setOpenPopover(null)
      setOverflowAction(null)
    }

    const deselect = () => {
      closeOverflow()
      onDeselect()
    }

    const onOverflowSelect = (action: BulkAction) => {
      if (action.popover) {
        setOverflowAction(action.title)

        return
      }

      action.buttonProps?.onClick?.(
        undefined as unknown as Parameters<
          NonNullable<ButtonProps['onClick']>
        >[0]
      )
      closeOverflow()
    }

    const openedOverflowPanel = overflowAction
      ? actions.find(({ title }) => title === overflowAction)
      : undefined

    const panelWithHeader = (
      action: BulkAction,
      close: () => void,
      backButton?: ReactNode
    ) => (
      <Flex vertical gap={token.marginXS}>
        <Flex
          align="center"
          gap={token.marginXXS}
          className={action.fillsPopover ? styles.flushPanelHeader : undefined}
        >
          {backButton}

          <span className={styles.label}>{action.title}</span>

          {action.hint && <InfoTooltip title={action.hint} />}
        </Flex>

        {action.popover?.(close)}
      </Flex>
    )

    useHotkeys(
      'esc',
      () => {
        deselect()

        const active = document.activeElement
        if (active instanceof HTMLElement) {
          active.blur()
        }
      },
      { enabled: count > 0, enableOnFormTags: true },
      [onDeselect]
    )

    return createPortal(
      <div ref={roomRef} style={positionStyles}>
        <AnimatePresence>
          {isVisible && (
            <motion.div
              key="bulk-actions-bar"
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 24 }}
              transition={{
                duration: parseFloat(token.motionDurationMid),
                ease: 'easeOut'
              }}
            >
              <Flex justify="center">
                <Flex
                  ref={barRef}
                  className={styles.bar}
                  gap={token.marginXS}
                  align="center"
                >
                  <Button
                    type="text"
                    iconPlacement="end"
                    icon={
                      <X
                        size={iconSizes.extraSmall}
                        color={token.colorTextQuaternary}
                      />
                    }
                    className={cx(styles.actionButton, styles.summaryButton)}
                    onClick={deselect}
                  >
                    <span className={styles.label}>{summary}</span>
                  </Button>

                  {selectAll && (
                    <Flex align="center" className={styles.selectAllSlot}>
                      {selectAll}
                    </Flex>
                  )}

                  {actions.length > 0 && (
                    <>
                      <Divider
                        orientation="vertical"
                        className={styles.divider}
                      />

                      <Flex
                        ref={actionsRef}
                        className={styles.actions}
                        align="center"
                        gap={token.marginXXS}
                      >
                        {visibleActions.map((action) => {
                          const {
                            title: blockedReason,
                            className,
                            icon,
                            ...buttonProps
                          } = action.buttonProps ?? {}

                          const button = (
                            <Tooltip key={action.title} title={blockedReason}>
                              <span className={styles.tooltipTrigger}>
                                <Button
                                  type="text"
                                  {...buttonProps}
                                  icon={icon}
                                  aria-label={action.title}
                                  className={cx(styles.actionButton, className)}
                                  classNames={{ root: styles.actionButton }}
                                >
                                  {action.title}
                                </Button>
                              </span>
                            </Tooltip>
                          )

                          if (!action.popover) {
                            return button
                          }

                          return (
                            <Popover
                              key={action.title}
                              open={openPanel === action.title}
                              onOpenChange={(open) =>
                                setOpenPopover(open ? action.title : null)
                              }
                              content={
                                action.hint
                                  ? panelWithHeader(action, () =>
                                      setOpenPopover(null)
                                    )
                                  : action.popover(() => setOpenPopover(null))
                              }
                              classNames={{
                                container: action.fillsPopover
                                  ? styles.flushPopover
                                  : styles.compactPopover
                              }}
                              trigger="click"
                              placement="top"
                              destroyOnHidden
                              zIndex={BAR_Z_INDEX + 1}
                            >
                              {button}
                            </Popover>
                          )
                        })}

                        {overflowActions.length > 0 && (
                          <Popover
                            open={openPanel === MORE_KEY}
                            onOpenChange={(open) => {
                              setOpenPopover(open ? MORE_KEY : null)
                              setOverflowAction(null)
                            }}
                            content={
                              openedOverflowPanel?.popover ? (
                                panelWithHeader(
                                  openedOverflowPanel,
                                  closeOverflow,
                                  <Button
                                    type="text"
                                    size="small"
                                    aria-label={t('back')}
                                    icon={
                                      <ChevronLeft size={iconSizes.small} />
                                    }
                                    onClick={() => setOverflowAction(null)}
                                  />
                                )
                              ) : (
                                <BulkActionsOverflow
                                  actions={overflowActions}
                                  onSelect={onOverflowSelect}
                                />
                              )
                            }
                            classNames={{
                              container: openedOverflowPanel?.fillsPopover
                                ? styles.flushPopover
                                : styles.compactPopover
                            }}
                            trigger="click"
                            placement="top"
                            destroyOnHidden
                            zIndex={BAR_Z_INDEX + 1}
                          >
                            <Button
                              type="text"
                              icon={<Ellipsis size={iconSizes.small} />}
                              aria-label={t('more_actions')}
                              className={styles.actionButton}
                              classNames={{ root: styles.actionButton }}
                            >
                              {t('more_actions')}
                            </Button>
                          </Popover>
                        )}
                      </Flex>

                      <div className={styles.measureClip} aria-hidden>
                        <div
                          ref={measureRef}
                          className={styles.measureRow}
                          style={{ gap: token.marginXXS }}
                        >
                          {actions.map(({ title, buttonProps }) => (
                            <Button
                              key={title}
                              type="text"
                              icon={buttonProps?.icon}
                              className={styles.actionButton}
                            >
                              {title}
                            </Button>
                          ))}

                          <Button
                            type="text"
                            icon={<Ellipsis size={iconSizes.small} />}
                            className={styles.actionButton}
                          >
                            {t('more_actions')}
                          </Button>
                        </div>
                      </div>
                    </>
                  )}

                  <Divider orientation="vertical" className={styles.divider} />

                  <Tooltip title={t('deselect')}>
                    <Button
                      type="text"
                      aria-label={t('clear_selection')}
                      className={styles.actionButton}
                      icon={<X size={CLOSE_ICON_SIZE} />}
                      onClick={deselect}
                    />
                  </Tooltip>
                </Flex>
              </Flex>
            </motion.div>
          )}
        </AnimatePresence>
      </div>,
      document.body
    )
  }
)
